import { useState, useEffect } from 'react';
import { toNumber, get } from 'lodash';
import { createSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import { CONFIG_BY_SPORTS } from 'scoreMetaConfig';
import { convertSecondsToTimeFormat } from 'utils';
import {
    selectMatchTime,
    selectTimerRunning,
    selectFilteredScores,
} from 'selectors';

const ONE_SECOND = 1000;
const TICK_INTERVAL = 1000;
const TICK_TIMEOUT = 60 * 1000;

interface Input {
    matchId: string;
    sportId: string;
}

interface Output {
    currentMatchPart?: string;
    matchTimeToShow: string | null;
}

const selectProps = createSelector(
    selectMatchTime,
    selectTimerRunning,
    selectFilteredScores,
    (matchTime, timerRunning, { currentMatchPart }) => {
        return {
            shouldShowTime: matchTime !== null,
            numericalMatchTime: toNumber(matchTime) || 0,
            isTimerRunning: timerRunning === '1',
            currentMatchPart,
        };
    }
);

const makeSelectProps = () => selectProps;

function useMatchTime({ matchId, sportId }: Input): Output {
    const config = get(CONFIG_BY_SPORTS, sportId, undefined);

    const isDecrement = config?.isTimeCountdown;
    const {
        numericalMatchTime,
        isTimerRunning,
        currentMatchPart,
        shouldShowTime,
    } = useMemoSelector(makeSelectProps(), { matchId });

    const [time, changeTime] = useState(numericalMatchTime);

    useEffect(() => changeTime(numericalMatchTime), [numericalMatchTime]);

    useEffect(() => {
        if (!isTimerRunning) return;

        const addition = isDecrement ? -TICK_INTERVAL : TICK_INTERVAL;

        const timerId = setInterval(() => {
            changeTime(prev => prev + addition);
        }, TICK_INTERVAL);

        const tickTimeoutId = setTimeout(
            () => clearInterval(timerId),
            TICK_TIMEOUT
        );

        return () => {
            clearInterval(timerId);
            clearTimeout(tickTimeoutId);
        };
    }, [isTimerRunning, isDecrement]);

    const finalTime = config?.isTimeAbsolute
        ? Math.abs(time)
        : Math.max(time, 0);

    const timeInSeconds = Math.trunc(finalTime / ONE_SECOND);

    const matchTimeToShow = config?.isTimeInSeconds
        ? convertSecondsToTimeFormat(timeInSeconds, [60, 1]) // NOTE: other option is Math.ceil,depends on back-office
        : Math.trunc((finalTime + 59000) / 60000) + '`';

    return {
        currentMatchPart: currentMatchPart?.text,
        matchTimeToShow:
            shouldShowTime && !currentMatchPart?.isTimeDisabled
                ? matchTimeToShow
                : null,
    };
}

export default useMatchTime;
